// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import VariablesEdit from '../forms/variables/Edit';

import { variable, VariableDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: VariableDO[];
}

export default class Variables extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      toggleItem: true,
      del: true,
      add: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    this.loadingStart();
    variable<VariableDO[]>('list')
      ?.then((resp: VariableDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'VE';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Variable Edit',
      component: VariablesEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'VARIABLE',
        title: 'Variable',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: VariableDO): void => {
    window.store.dispatch(removeTab('VE'));
    const rowId: number = value?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: VariableDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      variable('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      name: 'Name',
      unit: 'Unit',
      value: 'Code',
      graph: 'Plottable',
      alarm: 'Alarm Allowed'
    };
    const columStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      unit: { textAlign: 'left' },
      value: { textAlign: 'left' },
      graph: { textAlign: 'center' },
      alarm: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
